// src/components/network/messaging/MessagingContainer.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import MessagesList from './MessagesList';
import ConversationView from './ConversationView';
import NewMessage from './NewMessage';
import { db } from '../../../firebase/config';
import { doc, getDoc } from 'firebase/firestore';

const MessagingContainer = ({ initialConversationId, initialRecipientId, initialRecipientName }) => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [isNewMessage, setIsNewMessage] = useState(false);

  // For mobile view, track if we're showing messages list or conversation
  const [showMessagesListOnMobile, setShowMessagesListOnMobile] = useState(!initialConversationId && !initialRecipientId);

  // Load conversation from ID if provided
  useEffect(() => {
    const loadConversation = async () => {
      if (!initialConversationId || !currentUser) return;

      try {
        const conversationRef = doc(db, 'conversations', initialConversationId);
        const conversationSnap = await getDoc(conversationRef);

        if (conversationSnap.exists()) {
          const conversationData = conversationSnap.data();

          // Check if current user is a participant
          if (!conversationData.participants.includes(currentUser.uid)) {
            navigate('/network/messages');
            return;
          }

          // Find the other participant
          const otherParticipantId = conversationData.participants.find(id => id !== currentUser.uid);

          // Get participant info
          let participantInfo = { id: otherParticipantId, name: 'Unknown' };

          // Get participant details
          const businessRef = doc(db, 'businesses', otherParticipantId);
          const businessSnap = await getDoc(businessRef);

          if (businessSnap.exists()) {
            const businessData = businessSnap.data();
            participantInfo = {
              id: otherParticipantId,
              name: businessData.name,
              type: 'business',
              logoUrl: businessData.businessLogoUrl || null,
              industry: businessData.industry || null
            };
          } else {
            // Try to get user info as fallback
            const userRef = doc(db, 'users', otherParticipantId);
            const userSnap = await getDoc(userRef);

            if (userSnap.exists()) {
              const userData = userSnap.data();
              participantInfo = {
                id: otherParticipantId,
                name: userData.displayName || userData.email,
                type: 'user',
                email: userData.email,
                photoURL: userData.photoURL || null
              };
            }
          }

          // Set selected conversation
          setSelectedConversation({
            id: initialConversationId,
            ...conversationData,
            participant: participantInfo,
            unreadCount: conversationData.unreadCount?.[currentUser.uid] || 0
          });

          setShowMessagesListOnMobile(false);
        } else {
          // Conversation not found
          navigate('/network/messages');
        }
      } catch (err) {
        console.error('Error loading conversation:', err);
        navigate('/network/messages');
      }
    };

    loadConversation();
  }, [initialConversationId, currentUser, navigate]);

  // Handle initial recipient from profile Message button
  useEffect(() => {
    if (initialRecipientId && currentUser && !initialConversationId) {
      setIsNewMessage(true);
      setShowMessagesListOnMobile(false);
    }
  }, [initialRecipientId, currentUser, initialConversationId]);

  const handleSelectConversation = (conversation) => {
    setSelectedConversation(conversation);
    setIsNewMessage(false);
    setShowMessagesListOnMobile(false);

    // Update URL for direct linking
    navigate(`/network/messages/${conversation.id}`);
  };

  const handleStartNewMessage = () => {
    setSelectedConversation(null);
    setIsNewMessage(true);
    setShowMessagesListOnMobile(false);

    // Update URL
    navigate('/network/messages/new');
  };

  const handleBack = () => {
    if (isNewMessage) {
      setIsNewMessage(false);
    } else {
      setSelectedConversation(null);
    }
    setShowMessagesListOnMobile(true);

    // Update URL
    navigate('/network/messages');
  };

  if (!currentUser) {
    return (
      <div className="flex h-full flex-col justify-center items-center bg-gray-50 p-6 text-center">
        <h3 className="text-lg font-medium text-gray-900 mb-2">Sign In to Message</h3>
        <p className="text-gray-500 mb-4">
          Connect with businesses through private messaging
        </p>
        <a
          href="/login?redirect=/network/messages"
          className="px-4 py-2 bg-[#190192] text-white rounded-lg hover:bg-[#2C0DB5]"
        >
          Sign In
        </a>
      </div>
    );
  }

  return (
    <div className="h-full flex">
      {/* Messages list - hidden on mobile when conversation is open */}
      <div
        className={`
          ${showMessagesListOnMobile ? 'flex' : 'hidden'}
          md:flex h-full border-r border-gray-200 w-full
          md:w-1/3 overflow-hidden
        `}
      >
        <MessagesList
          onSelectConversation={handleSelectConversation}
          onNewMessage={handleStartNewMessage}
        />
      </div>

      {/* Conversation view or new message - shown on mobile when conversation is open */}
      <div
        className={`
          ${showMessagesListOnMobile ? 'hidden' : 'flex'}
          md:flex h-full flex-1 md:flex-[2] overflow-hidden
        `}
      >
        {isNewMessage ? (
          <NewMessage
            onSelectConversation={handleSelectConversation}
            onBack={handleBack}
            initialRecipientId={initialRecipientId}
            initialRecipientName={initialRecipientName}
          />
        ) : (
          <ConversationView
            conversation={selectedConversation}
            onBack={handleBack}
          />
        )}
      </div>
    </div>
  );
};

export default MessagingContainer;
